import { CityState } from './types';

export class EconomyEngine {
  private readonly WAGE = 50; 
  private readonly ROAD_MAINTENANCE = 1; 
  private readonly CIVIC_MAINTENANCE = 10; 

  /**
   * Ticks causative tax loops, maintenance costs, and RCI demands.
   */
  public tickEconomy(state: CityState) {
    let totalPopulation = 0;
    let totalJobCapacity = 0;

    // Count statistics from completed zoned buildings
    for (const key of Object.keys(state.buildings)) {
      const b = state.buildings[key];
      if (b.stage === 'completed') {
        if (b.zone === 'residential') {
          totalPopulation += b.occupancy;
        } else if (b.zone === 'commercial' || b.zone === 'industrial' || b.zone === 'office') {
          totalJobCapacity += b.capacity;
        }
      }
    }

    const employed = Math.min(totalPopulation, totalJobCapacity);
    state.population = totalPopulation;
    state.jobs = totalJobCapacity;

    // 1. Tax Collections (Resident income, business and corporate taxes)
    const earnings = employed * this.WAGE;
    const commercialTax = employed * 1.5 * this.WAGE * (state.taxRate * 1.2);
    const industrialTax = employed * 2.0 * this.WAGE * (state.taxRate * 1.5);
    const residentTax = earnings * state.taxRate;

    const taxRevenue = Math.floor(residentTax + commercialTax + industrialTax);
    state.treasury += taxRevenue;

    // 2. Public Infrastructures Maintenance
    const roadsCount = Object.keys(state.roads).length;
    let civicCount = 0;

    for (const key of Object.keys(state.buildings)) {
      const b = state.buildings[key];
      if (b.zone === 'park' || b.zone === 'civic') {
        civicCount++;
      }
    }

    const totalMaintenance = (roadsCount * this.ROAD_MAINTENANCE) + (civicCount * this.CIVIC_MAINTENANCE);
    state.treasury -= totalMaintenance;

    // 3. Happiness calculations
    this.calculateHappiness(state, employed, totalPopulation);

    // 4. Recalculate demands (Residential, Commercial, Industrial)
    this.updateDemand(state, totalPopulation, totalJobCapacity);
  }

  private calculateHappiness(state: CityState, employed: number, population: number) {
    let happiness = 75; // baseline

    if (population > 0) {
      const employmentRate = employed / population;
      if (employmentRate < 0.8) {
        happiness -= (0.8 - employmentRate) * 100; // heavy penalty for unemployment
      } else {
        happiness += (employmentRate - 0.8) * 50;
      }
    }

    if (state.taxRate > 0.15) {
      happiness -= (state.taxRate - 0.15) * 200; // citizen dislike high taxes
    } else if (state.taxRate < 0.10) {
      happiness += (0.10 - state.taxRate) * 100;
    }

    if (state.treasury < 0) {
      happiness -= 20; // deficit penalty
    }

    state.happiness = Math.max(0, Math.min(100, Math.round(happiness)));
  }

  private updateDemand(state: CityState, population: number, jobs: number) {
    let rDemand = state.demand.residential;
    let cDemand = state.demand.commercial;
    let iDemand = state.demand.industrial;

    // R demand is driven by available job openings
    if (jobs > population) {
      rDemand += 15;
    } else {
      rDemand -= 5;
    }

    // C demand is driven by local shoppers (population)
    if (population > jobs) {
      cDemand += 10;
    } else {
      cDemand -= 2;
    }

    // Industrial demand is driven by labor surplus (unemployment)
    if (population > jobs) {
      iDemand += 8;
    } else {
      iDemand -= 4;
    }

    // Starter boost
    if (population === 0 && jobs === 0) {
      rDemand = Math.max(50, rDemand);
      cDemand = Math.max(30, cDemand);
      iDemand = Math.max(20, iDemand);
    }

    // Apply happiness multiplier
    const happinessMult = state.happiness / 100;
    rDemand *= happinessMult;
    cDemand *= happinessMult;
    iDemand *= happinessMult;

    // High taxation dampens demand
    if (state.taxRate > 0.20) {
      rDemand -= 15;
      cDemand -= 25;
      iDemand -= 35;
    }

    state.demand.residential = Math.max(0, Math.min(100, Math.round(rDemand)));
    state.demand.commercial = Math.max(0, Math.min(100, Math.round(cDemand)));
    state.demand.industrial = Math.max(0, Math.min(100, Math.round(iDemand)));
  }
}
